<template>
  <v-navigation-drawer
    :mini-variant="miniVariant"
    :mini-variant-width="72"
    :clipped="clipped"
    v-model="drawer"
    fixed
    app
    class="drawer-left"
    :class="{ 'drawer-left--rail': rail }"
    width="255"
  >
    <!-- ============ Store header (prepend) — same shell as drawerLeft ============ -->
    <template v-slot:prepend>
      <div class="ps-store-header" :class="{ 'ps-store-header--rail': rail }">
        <span class="ps-store-logo">
          <img
            v-if="storeLogo"
            :src="storeLogo"
            alt=""
            class="ps-store-logo__img"
          />
          <v-icon v-else class="ps-store-logo__icon">fal fa-store</v-icon>
        </span>
        <div v-if="!rail" class="ps-store-meta">
          <p class="ps-store-name">{{ storeName }}</p>
          <p v-if="branchName" class="ps-store-branch">{{ branchName }}</p>
        </div>
      </div>
    </template>

    <!-- ============ Nav ============ -->
    <v-list v-if="menus.length" class="ps-menu">
      <template v-if="rail">
        <v-tooltip
          v-for="(item, i) in menus"
          :key="'rail-' + i"
          right
          content-class="ps-rail-tooltip"
        >
          <template v-slot:activator="{ on }">
            <v-list-item
              :to="item.to"
              link
              class="ps-rail-item"
              v-on="on"
            >
              <span class="ps-menu-icon">
                <v-icon v-if="item.icon" :size="16">{{ item.icon }}</v-icon>
              </span>
            </v-list-item>
          </template>
          <span>{{ $t(item.name) }}</span>
        </v-tooltip>
      </template>
      <template v-else>
        <v-list-item
          v-for="(item, i) in menus"
          :key="i"
          :to="item.to"
          link
        >
          <v-list-item-icon>
            <span class="ps-menu-icon">
              <v-icon v-if="item.icon" :size="16">{{ item.icon }}</v-icon>
            </span>
          </v-list-item-icon>
          <v-list-item-title>{{ $t(item.name) }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>

    <!-- ============ User footer (append) ============ -->
    <template v-slot:append>
      <div class="ps-user-wrap" :class="{ 'ps-user-wrap--rail': rail }">
        <div class="ps-user" :class="{ 'ps-user--rail': rail }">
          <span class="ps-user-avatar">
            <img
              v-if="userAvatar"
              :src="userAvatar"
              alt=""
              class="ps-user-avatar__img"
            />
            <span v-else class="ps-user-avatar__initials">{{ initials }}</span>
          </span>
          <template v-if="!rail">
            <div class="ps-user-meta">
              <p class="ps-user-name">{{ displayName }}</p>
              <p class="ps-user-role">{{ userRole(Number(user.role)) }}</p>
            </div>
            <button
              class="ps-logout-btn"
              :title="$t('logout')"
              :aria-label="$t('logout')"
              @click="logoutMethod"
            >
              <v-icon small>fal fa-sign-out-alt</v-icon>
            </button>
          </template>
          <v-tooltip v-else right content-class="ps-rail-tooltip">
            <template v-slot:activator="{ on }">
              <button
                class="ps-logout-btn ps-logout-btn--rail"
                v-on="on"
                :aria-label="$t('logout')"
                @click="logoutMethod"
              >
                <v-icon small>fal fa-sign-out-alt</v-icon>
              </button>
            </template>
            <span>{{ $t("logout") }}</span>
          </v-tooltip>
        </div>
      </div>
    </template>
    <app-custom-dialog
      v-model="logoutDialog"
      title="confirm_logout"
      width="420px"
      cancel-text="cancel"
      submit-text="logout"
      submit-color="error"
      @submit="confirmLogout"
    >
      <div slot="activator" class="logout-confirmation">
        {{ $t("want_to_logout") }}
      </div>
    </app-custom-dialog>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from "vuex";
import { myMixin } from "@/mixin/main";

const getDrawer = () => {
  const drawer = localStorage.getItem("drawer");
  if (drawer) {
    return drawer === "true" ? true : false;
  } else {
    return true;
  }
};

const getMini = () => localStorage.getItem("drawerMini") === "true";

export default {
  props: {
    menus: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      clipped: false,
      drawer: getDrawer(),
      miniVariant: getMini(),
      dialog: false,
      logoutDialog: false,
    };
  },

  watch: {
    drawer(val) {
      localStorage.setItem("drawer", val);
      this.$emit("toggle", this.expanded);
    },
    miniVariant(val) {
      localStorage.setItem("drawerMini", val ? "true" : "false");
      this.$emit("toggle", this.expanded);
    },
  },
  mixins: [myMixin],
  created() {
    if (this.$vuetify.breakpoint.mobile) {
      this.drawer = false;
    } else {
      this.drawer = true;
    }
  },

  mounted() {
    // Report the initial state so header arrows can point the right way
    this.$emit("toggle", this.expanded);
    document.addEventListener("keydown", this.onSidebarShortcut);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.onSidebarShortcut);
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
      isLoggedIn: "auth/isLoggedIn",
    }),
    rail() {
      return this.miniVariant;
    },
    isDesktop() {
      return this.$vuetify.breakpoint.mdAndUp;
    },
    /** True when the sidebar is shown at full width (arrow points left). */
    expanded() {
      return this.isDesktop ? !this.miniVariant : this.drawer;
    },
    storeName() {
      return (
        this.user?.stores?.name ||
        this.user?.stores?.store_id?.name ||
        "PosSmart"
      );
    },
    storeLogo() {
      const logo =
        this.user?.stores?.logo || this.user?.stores?.store_id?.logo;
      return logo ? Help.img(logo) : null;
    },
    branchName() {
      return (
        this.user?.branch_id?.name ||
        this.user?.stores?.store_id?.name ||
        ""
      );
    },
    displayName() {
      if (this.user) {
        const parts = [this.user.first_name, this.user.last_name].filter(
          Boolean,
        );
        return parts.length ? parts.join(" ") : "PosSmart";
      }
      return "PosSmart";
    },
    initials() {
      const name = this.displayName.trim();
      const parts = name.split(/\s+/);
      return ((parts[0] && parts[0][0]) || "P") +
        ((parts[1] && parts[1][0]) || "");
    },
    userAvatar() {
      return this.user?.avatar ? Help.img(this.user.avatar) : null;
    },
  },
  methods: {
    toggleSidebar() {
      if (this.isDesktop) {
        this.miniVariant = !this.miniVariant;
      } else {
        this.drawer = !this.drawer;
      }
    },
    onSidebarShortcut(event) {
      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }
      if (event.key !== "b" && event.key !== "B") {
        return;
      }
      const el = event.target;
      if (!el || !(el instanceof Element)) {
        return;
      }
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        return;
      }
      if (el.isContentEditable) {
        return;
      }
      event.preventDefault();
      this.toggleSidebar();
    },
    logoutMethod() {
      this.logoutDialog = true;
    },
    async confirmLogout() {
      try {
        await this.$store.dispatch("auth/logout", this);
        window.location.href = "/";
      } catch (error) {
        console.log(error);
      } finally {
        this.logoutDialog = false;
      }
    },
  },
};
</script>

<style scoped>
/* ============================================================
   PosSmart v2 sidebar — same design shell as drawerLeft.vue
   (white shell, brand #5b39c9 active pill, slate text, 72px rail)
   ============================================================ */

.drawer-left {
  background: #ffffff;
}

.theme--dark .drawer-left {
  background: #171b21;
}

/* Icon slot: fixed 18px so FA glyphs align */
.ps-menu-icon {
  width: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ---- Store header (prepend) ---- */
/* ---- Store header (prepend) ----
   Total height matches the sale page app bar: 64px content + the 1px bottom
   border that main.css paints on .v-navigation-drawer__prepend
   (#e0e0e0 light / #2f333a dark) = 65px, same line as the app bar. */
.ps-store-header {
  height: 64px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  flex-shrink: 0;
}

/* main.css gives the drawer prepend 1px top/bottom padding, which pushes the
   header line 1px below the store header — remove it so the line sits flush
   at the 65px mark, exactly like the app bar's border. */
.drawer-left ::v-deep .v-navigation-drawer__prepend {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.theme--dark .ps-store-header {
  border-bottom-color: #2f333a;
}

.ps-store-header--rail {
  justify-content: center;
  padding: 0;
  gap: 0;
}

.ps-store-logo {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #f3f0fc;
  color: #5b39c9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.ps-store-logo__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ps-store-logo__icon {
  color: #5b39c9 !important;
  font-size: 18px;
}

.ps-store-meta {
  min-width: 0;
}

.ps-store-name {
  font-weight: 900;
  font-size: 14px;
  line-height: 1.25;
  color: #141021;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .ps-store-name {
  color: #e5e7eb;
}

.ps-store-branch {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .ps-store-branch {
  color: rgba(226, 232, 240, 0.6);
}

/* ---- Menu items ---- */
.drawer-left ::v-deep .v-list-item {
  min-height: 44px;
  padding: 0 12px;
  margin: 0 12px 2px;
  border-radius: 12px;
  border: 1px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}

/* Match the hover overlay radius to the item's pill (see drawerLeft.vue) */
.drawer-left ::v-deep .v-list-item:before {
  border-radius: inherit;
}

.drawer-left ::v-deep .v-list-item__icon {
  margin-right: 12px !important;
}

.drawer-left ::v-deep .v-list-item .v-icon {
  color: #64748b;
}

.theme--dark .drawer-left ::v-deep .v-list-item .v-icon {
  color: rgba(226, 232, 240, 0.7);
}

.drawer-left ::v-deep .v-list-item__title {
  color: #64748b;
  font-weight: 700;
  font-size: 0.875rem;
}

.theme--dark .drawer-left ::v-deep .v-list-item__title {
  color: rgba(226, 232, 240, 0.8);
}

.drawer-left ::v-deep .v-list-item:hover:not(.v-list-item--active) {
  background: #f8fafc;
}

.theme--dark .drawer-left ::v-deep .v-list-item:hover:not(.v-list-item--active) {
  background: rgba(148, 163, 184, 0.08);
}

.drawer-left ::v-deep .v-list-item:hover:not(.v-list-item--active) .v-list-item__title {
  color: #5b39c9;
}

.drawer-left ::v-deep .v-list-item:hover:not(.v-list-item--active) .v-icon {
  color: #5b39c9;
}

.drawer-left ::v-deep .v-list-item--active {
  background: #f3f0fc !important;
  border-color: rgba(91, 57, 201, 0.2);
}

.drawer-left ::v-deep .v-list-item--active .v-list-item__title {
  color: #5b39c9;
  font-weight: 900;
}

.drawer-left ::v-deep .v-list-item--active .v-icon {
  color: #5b39c9 !important;
}

/* ---- Collapsed rail ---- */
.drawer-left--rail ::v-deep .v-list-item {
  margin: 0 10px 2px;
  padding: 0 !important;
  justify-content: center;
}

.drawer-left--rail ::v-deep .v-list-item:hover:not(.v-list-item--active) {
  background: #f8fafc;
}

.theme--dark
  .drawer-left--rail
  ::v-deep
  .v-list-item:hover:not(.v-list-item--active) {
  background: rgba(148, 163, 184, 0.08);
}

/* ---- User footer ---- */
.ps-user-wrap {
  border-top: 1px solid #e2e8f0;
}

.theme--dark .ps-user-wrap {
  border-top-color: #2f333a;
}

.ps-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
}

.ps-user--rail {
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
}

.ps-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #5b39c9;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.ps-user-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ps-user-avatar__initials {
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
}

.ps-user-meta {
  min-width: 0;
  flex: 1;
}

.ps-user-name {
  font-weight: 900;
  font-size: 14px;
  line-height: 1.25;
  color: #141021;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .ps-user-name {
  color: #e5e7eb;
}

.ps-user-role {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .ps-user-role {
  color: rgba(226, 232, 240, 0.6);
}

.ps-logout-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  border-radius: 8px;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s ease, border-color 0.15s ease,
    background-color 0.15s ease;
}

/* Soft red sign-out button (same bordered shape as drawer-cart-header__icon-btn) */
.ps-logout-btn:hover {
  color: #b91c1c !important;
  border-color: #fca5a5 !important;
  background: #fee2e2 !important;
}

.theme--dark .ps-logout-btn {
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.35);
  color: #f87171;
}

.theme--dark .ps-logout-btn:hover {
  color: #fca5a5 !important;
  border-color: rgba(239, 68, 68, 0.55) !important;
  background: rgba(239, 68, 68, 0.2) !important;
}

.ps-logout-btn--rail {
  margin: 0 auto;
}
</style>

<style>
/* Rail tooltip bubble — see drawerLeft.vue for the shared global style */
.ps-rail-tooltip {
  background: #141021 !important;
  color: #ffffff !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  padding: 7px 10px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(20, 16, 33, 0.3) !important;
  opacity: 1 !important;
}
</style>
